import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import * as Sentry from "@sentry/react";
import { supabase } from "@/integrations/supabase/client";
import { useCurrentOrgId } from "@/hooks/auth";

const INVALIDATION_COOLDOWN_MS = 1000;

const CRM_ROUTES = ["/deals", "/leads", "/activities"];

function isCrmRoute(pathname: string) {
  return CRM_ROUTES.some((route) => pathname.startsWith(route));
}

/**
 * A linha de `activities` aparece no card do Kanban?
 *
 * Espelha o filtro do embed em `useDealsByStage` — `status IN (pending,paused)`,
 * `type <> 'note'`, `date NOT NULL` — que alimenta o chip do `ActivityPopover`.
 * Atividade nascida `done` (o caso do registro automático de ligações e das notas)
 * não muda um pixel lá.
 *
 * Medição de 25/08 (7 dias, contando "segundos com evento", a unidade real depois do
 * cooldown): das 21.938 rodadas de invalidação disparadas por `activities`, só 1.071
 * (4,9%) envolviam atividade capaz de alterar o card. As outras 20.867 faziam TODO
 * cliente aberto da org rebuscar as 7 colunas para redesenhar a mesma tela.
 *
 * Exportado para teste: se este predicado errar para menos, o Kanban perde atualização
 * em silêncio. Ao mexer no filtro do embed, mexa aqui também.
 */
export function affectsKanbanCard(row?: Record<string, unknown> | null): boolean {
  if (!row) return false;
  if (row.status !== "pending" && row.status !== "paused") return false;
  if (row.type === "note") return false;
  return row.date != null;
}

export function useCrmRealtime() {
  const { orgId } = useCurrentOrgId();
  const queryClient = useQueryClient();
  const location = useLocation();
  const lastInvalidationRef = useRef<Record<string, number>>({});

  useEffect(() => {
    if (!orgId || !isCrmRoute(location.pathname)) return;

    const passesCooldown = (key: string) => {
      const now = Date.now();
      const last = lastInvalidationRef.current[key] ?? 0;
      if (now - last < INVALIDATION_COOLDOWN_MS) return false;
      lastInvalidationRef.current[key] = now;
      return true;
    };

    const invalidateWithCooldown = (key: string, queryKey: readonly unknown[]) => {
      if (!passesCooldown(key)) return;
      queryClient.invalidateQueries({ queryKey });
    };

    // Variante por predicado: usada onde invalidar pelo PREFIXO atingiria queries
    // caras que a linha alterada não afeta. Ver invalidateActivities.
    const invalidateWithCooldownPredicate = (
      key: string,
      predicate: (query: { queryKey: readonly unknown[] }) => boolean
    ) => {
      if (!passesCooldown(key)) return;
      queryClient.invalidateQueries({ predicate });
    };

    const invalidateDeals = () => {
      invalidateWithCooldown("deals:list", ["deals"]);
      invalidateWithCooldown("deals:infinite", ["deals-infinite"]);
      invalidateWithCooldown("deals:single", ["deal"]);
      invalidateWithCooldown("deals:kanban", ["deals-by-stage"]);
      invalidateWithCooldown("deals:stage-counts", ["deal-stage-counts"]);
      invalidateWithCooldown("deals:stage-counts-v2", ["deals-count-by-stage"]);
      invalidateWithCooldown("deals:stats", ["deal-stats"]);
      invalidateWithCooldown("deals:monthly", ["deals-by-month"]);
    };

    const invalidateLeads = () => {
      invalidateWithCooldown("leads:list", ["leads"]);
      invalidateWithCooldown("leads:infinite", ["leads-infinite"]);
      invalidateWithCooldown("leads:count", ["leads-count"]);
      invalidateWithCooldown("leads:single", ["lead"]);
      invalidateWithCooldown("leads:monthly", ["leads-by-month"]);
      invalidateWithCooldown("leads:search", ["lead-search"]);
      invalidateWithCooldown("leads:by-id", ["lead-by-id"]);
    };

    const invalidateActivities = (payload?: {
      new?: Record<string, unknown> | null;
      old?: Record<string, unknown> | null;
    }) => {
      const row = (payload?.new ?? payload?.old ?? {}) as Record<string, unknown>;
      const changedDealId = typeof row.deal_id === "string" ? row.deal_id : null;
      const changedLeadId = typeof row.lead_id === "string" ? row.lead_id : null;

      // Lista de /activities — key ["activities", orgId, params]. Segue invalidando
      // inteira: é uma query por tela e o usuário está olhando para ela.
      invalidateWithCooldownPredicate(
        "activities:list",
        (q) => q.queryKey[0] === "activities" && q.queryKey[1] !== "deal"
      );

      // Timeline do negócio — key ["activities","deal",dealId,leadId,orgId,flag].
      //
      // ANTES: invalidada pelo prefixo ["activities"], ou seja QUALQUER escrita de
      // atividade em QUALQUER lugar da org refazia as 3 queries pesadas de
      // useActivitiesByDeal em todo cliente com um negócio aberto. Com cadências e
      // automações escrevendo atividades o dia inteiro, esse era o amplificador que
      // levava a busca multi-deal a 40+ chamadas por 25s vindas de uma só org.
      // Ver incidente de performance 03/08.
      //
      // AGORA: só as timelines que a linha alterada de fato afeta. Cooldown por
      // deal/lead para que um negócio movimentado não engula a atualização de outro.
      if (changedDealId || changedLeadId) {
        invalidateWithCooldownPredicate(
          `activities:deal-timeline:${changedDealId ?? changedLeadId}`,
          (q) => {
            const k = q.queryKey;
            if (k[0] !== "activities" || k[1] !== "deal") return false;
            return (
              (changedDealId !== null && k[2] === changedDealId) ||
              (changedLeadId !== null && k[3] === changedLeadId)
            );
          }
        );
      } else {
        // Sem deal_id/lead_id no payload não dá para saber qual timeline mudou —
        // atividade solta na org, sem negócio nem lead. Cai no comportamento antigo:
        // abrangente, porém correto. É raro o bastante para não recriar a tempestade
        // de invalidação.
        // (Antes este comentário atribuía o caso a DELETE com REPLICA IDENTITY default.
        // Não procede: `activities` está com REPLICA IDENTITY FULL — conferido em
        // pg_class.relreplident em 25/08 — então o DELETE traz a linha antiga inteira.)
        invalidateWithCooldown("activities:deal-timeline:all", ["activities", "deal"]);
      }

      invalidateWithCooldown("activities:lead", ["lead-activities"]);
      invalidateWithCooldown("activities:deal", ["deal-activities"]);
      invalidateWithCooldown("activities:stats", ["activity-stats"]);

      // `dashboard-activities` NÃO entra aqui: a única consumidora é pages/Dashboard.tsx
      // (rota /dashboard-legado), e este canal só existe nas CRM_ROUTES. A query nunca
      // está montada quando o evento chega, e React Query não refaz query desmontada —
      // era invalidação em chave morta.

      // Kanban: só quando a linha alterada de fato aparece no card. Avaliado no estado
      // NOVO e no ANTIGO — a transição pending→done tira o chip, então precisa invalidar
      // mesmo que só o `old` passe pelo filtro. Em DELETE o `old` vem completo porque
      // `activities` está com REPLICA IDENTITY FULL.
      //
      // Consolidado aqui (antes vinha de useActivitiesRealtimeSync, um 2º canal realtime
      // na mesma tabela) para evitar subscription duplicada de WAL.
      if (affectsKanbanCard(payload?.new) || affectsKanbanCard(payload?.old)) {
        invalidateWithCooldown("deals:kanban", ["deals-by-stage"]);
      }

      // `deal-stage-counts` NÃO entra aqui. As funções que alimentam o badge
      // (`deal_stage_counts_for_pipeline` e `..._filtered`) contam DEALS por etapa e não
      // referenciam `activities` em nenhum ponto — conferido no corpo das funções em
      // produção em 25/08. Nenhuma mudança de atividade pode alterar aquele número.
      // Automação que move o deal ao registrar atividade também escreve em `deals`, e aí
      // o invalidateDeals cobre o badge. Era 69,7% dos gatilhos da chave, todos inertes.
    };

    // Ligações (histórico do lead e chips de qualificação/gravação/transcrição nas
    // timelines). O sonax-webhook e o batch de transcrição escrevem em `ligacoes` depois
    // que a atividade já existe; sem isto a tela só via a mudança pelo polling.
    const invalidateLigacoes = () => {
      invalidateWithCooldown("ligacoes:history", ["ligacoes"]);
      invalidateWithCooldown("ligacoes:qualifiers-timeline", ["ligacoes-qualifiers-timeline"]);
      invalidateWithCooldown("ligacoes:qualifiers", ["ligacoes-qualifiers"]);
    };

    const channel = supabase
      .channel(`crm-core-${orgId}`)
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "deals", filter: `org_id=eq.${orgId}` },
        invalidateDeals
      )
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "leads", filter: `org_id=eq.${orgId}` },
        invalidateLeads
      )
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "activities", filter: `org_id=eq.${orgId}` },
        invalidateActivities
      )
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "ligacoes", filter: `org_id=eq.${orgId}` },
        invalidateLigacoes
      )
      .subscribe((status) => {
        if (import.meta.env.DEV) {
          console.info(`[CRM Realtime] crm-core-${orgId}:`, status);
        }

        if (status === "CHANNEL_ERROR" || status === "TIMED_OUT") {
          Sentry.captureMessage("CRM realtime channel unhealthy", {
            level: "warning",
            tags: { module: "realtime", channel: `crm-core-${orgId}`, status },
          });
        }
      });

    return () => {
      supabase.removeChannel(channel);
    };
  }, [orgId, location.pathname, queryClient]);
}
